import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Camera, RotateCcw, Sparkles, ShieldCheck, ArrowLeft, Loader2, ImageOff, Users } from "lucide-react";
import Logo from "../components/Logo";

/**
 * Guest-facing "Find My Photos" — Grand-tier AI face grouping flow.
 *
 * Group-selfie support: multiple faces are detected in the selfie and
 * the guest can filter matches by "Everyone Together", "Just me",
 * "Just Face 2", etc. Each detected face shows a small circular crop
 * from the original selfie so friends can figure out who's who.
 *
 * Steps: consent → camera → searching → results → (retry loop).
 */
const FindMyPhotosPage = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [step, setStep] = useState("consent"); // consent | camera | searching | results | error
  const [consented, setConsented] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [result, setResult] = useState(null); // {query_faces, matches, ...}
  const [activeFilter, setActiveFilter] = useState("all"); // "all" | "together" | face-index (number)
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/events/${slug}`);
        setEvent(r.data);
      } catch {
        setErrMsg("This event could not be found.");
        setStep("error");
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (step !== "camera") return;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (err) {
        console.debug("[find-my-photos] camera unavailable:", err?.message);
      }
    })();
    return () => {
      cancelled = true;
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [step]);

  const stopStream = () => {
    const s = streamRef.current;
    if (s) s.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const submitSelfie = async (blob) => {
    setStep("searching"); setErrMsg(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", blob, "selfie.jpg");
      fd.append("consent", "true");
      const r = await axios.post(`${API}/public/events/${slug}/faces/find`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000, // face matching can take 30-60s on cold-start
      });
      setResult(r.data);
      const hasTogether = (r.data.matches || []).some((m) => (m.matched_faces || []).length > 1);
      setActiveFilter(hasTogether ? "together" : "all");
      setStep("results");
    } catch (err) {
      // Sanitize error: Cloudflare 5xx responses contain raw HTML that
      // must NEVER be shown to guests. Only surface our own JSON `detail`
      // strings — everything else becomes a friendly generic message.
      const status = err?.response?.status;
      const data = err?.response?.data;
      const isJsonDetail = data && typeof data === "object" && typeof data.detail === "string";
      let friendly;
      if (isJsonDetail && status && status < 500) {
        // Our API returned a proper 4xx with a helpful message (e.g., 422
        // "we couldn't find a face"). Safe to show verbatim.
        friendly = data.detail;
      } else if (status && status >= 500) {
        friendly = "The face-matching engine is warming up. Please try again in about 30 seconds — the first search after a break takes a bit longer.";
      } else if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
        friendly = "The search took too long. Try again — it should be faster this time.";
      } else {
        friendly = "We hit a snag. Try again in good light, and make sure your face fills the circle.";
      }
      setErrMsg(friendly);
      setStep("camera");
    }
  };

  const snapAndSend = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const size = Math.min(v.videoWidth, v.videoHeight) || 640;
    c.width = size; c.height = size;
    const ctx = c.getContext("2d");
    const sx = (v.videoWidth - size) / 2;
    const sy = (v.videoHeight - size) / 2;
    ctx.drawImage(v, sx, sy, size, size, 0, 0, size, size);
    const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.9));
    stopStream();
    if (blob) submitSelfie(blob);
  };

  const onFileFallback = async (e) => {
    const f = e.target.files?.[0];
    if (f) submitSelfie(f);
  };

  const reset = () => {
    setResult(null); setErrMsg(""); setStep("camera");
  };

  // ------ Derived: filter matches based on active filter -----------
  const filteredMatches = useMemo(() => {
    if (!result) return [];
    if (activeFilter === "all") return result.matches;
    if (activeFilter === "together") {
      // Only photos where ALL detected query faces appear
      const totalFaces = (result.query_faces || []).length;
      return result.matches.filter((m) => (m.matched_faces || []).length === totalFaces);
    }
    // Numeric face index → only photos matching that specific face
    return result.matches.filter((m) => (m.matched_faces || []).includes(activeFilter));
  }, [result, activeFilter]);

  const themeColor = event?.theme_color || "#e8846b";
  const faceCount = result?.query_faces?.length || 0;
  const isGroup = faceCount > 1;
  // For each face, how many matches include it? (For badges on face tiles.)
  const perFaceCount = useMemo(() => {
    if (!result) return [];
    return (result.query_faces || []).map((_, i) =>
      result.matches.filter((m) => (m.matched_faces || []).includes(i)).length
    );
  }, [result]);
  const togetherCount = useMemo(() => {
    if (!result) return 0;
    const total = (result.query_faces || []).length;
    return result.matches.filter((m) => (m.matched_faces || []).length === total).length;
  }, [result]);

  return (
    <div data-testid="find-my-photos-page" className="min-h-screen bg-cream-100">
      <div className="relative h-40 sm:h-48 flex items-center justify-center overflow-hidden"
           style={{ background: `linear-gradient(135deg, ${themeColor}22, ${themeColor}44)` }}>
        <div className="absolute inset-0 opacity-40" style={{ background: `radial-gradient(circle at 30% 30%, ${themeColor}66, transparent 60%)` }} />
        <div className="absolute top-4 left-4 z-10">
          <Logo size="sm" tint="white" className="drop-shadow-lg" />
        </div>
        <Link to={`/g/${slug}`} className="absolute top-4 right-4 text-navy-700/70 hover:text-navy-700 text-sm inline-flex items-center gap-1 bg-white/70 backdrop-blur px-3 py-1 rounded-full">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to gallery
        </Link>
        <div className="relative text-center px-4">
          <div className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-navy-700/70 font-semibold">
            <Sparkles className="w-3.5 h-3.5" /> AI Find My Photos
          </div>
          <h1 className="font-serif text-navy-700 text-3xl sm:text-4xl mt-1 italic">
            {event?.name || "Snap a selfie"}
          </h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-5 -mt-10 pb-12 space-y-4">
        {step === "consent" && (
          <div data-testid="fmp-consent" className="bg-white rounded-3xl soft-shadow p-6">
            <ShieldCheck className="w-8 h-8 text-coral-300" />
            <h2 className="font-serif italic text-navy-700 text-2xl mt-3">Just one thing first…</h2>
            <p className="text-sm text-navy-700/70 mt-2 leading-relaxed">
              To find only your photos, we&apos;ll take a selfie, measure the shape of your face, and match it against every photo on this gallery.
              <strong> Bring your friends in the frame</strong> to search for the whole group at once — we&apos;ll show you which photos you&apos;re in together and which ones are just you.
            </p>
            <ul className="mt-4 text-sm text-navy-700/70 space-y-2">
              <li className="flex gap-2"><Users className="w-4 h-4 text-coral-300 shrink-0 mt-0.5" /> Up to 4 faces per selfie</li>
              <li className="flex gap-2"><Sparkles className="w-4 h-4 text-coral-300 shrink-0 mt-0.5" /> Selfie is processed on the fly and discarded</li>
              <li className="flex gap-2"><Sparkles className="w-4 h-4 text-coral-300 shrink-0 mt-0.5" /> No account, no login needed</li>
            </ul>
            <label className="flex items-start gap-2 mt-5 text-sm text-navy-700 cursor-pointer" data-testid="fmp-consent-label">
              <input type="checkbox" checked={consented} onChange={(e) => setConsented(e.target.checked)}
                data-testid="fmp-consent-checkbox"
                className="mt-1 w-4 h-4 accent-coral-300" />
              I agree to use my (and my friends&apos;) faces to find our photos.
            </label>
            <button
              onClick={() => {
                if (!consented) return;
                setStep("camera");
                // Fire-and-forget warm-up so the FaceAnalysis model loads
                // in the background while the guest aims the camera. If
                // this fails the first /find call will still cold-start
                // itself — no user-visible impact.
                axios.post(`${API}/public/events/${slug}/faces/warmup`, {},
                  { timeout: 60000 })
                  .catch((e) => console.debug("[fmp] warmup skipped:", e?.message));
              }}
              disabled={!consented}
              data-testid="fmp-consent-continue"
              className="mt-5 w-full bg-coral-300 text-white py-3 rounded-full font-semibold disabled:opacity-50 hover:bg-coral-400"
            >
              Continue → Snap a selfie
            </button>
          </div>
        )}

        {step === "camera" && (
          <div data-testid="fmp-camera" className="bg-white rounded-3xl soft-shadow p-5 space-y-4">
            <div className="aspect-square bg-cream-200 rounded-2xl overflow-hidden relative">
              <video ref={videoRef} playsInline muted autoPlay
                className="w-full h-full object-cover scale-x-[-1]"
                data-testid="fmp-video" />
              <div className="absolute inset-6 rounded-3xl border-4 border-white/60 pointer-events-none" />
              <div className="absolute bottom-3 inset-x-0 text-center text-white text-xs bg-black/40 mx-6 py-1 rounded-full backdrop-blur">
                <Users className="w-3.5 h-3.5 inline mr-1" /> Fit everyone in the frame
              </div>
            </div>
            {errMsg && (
              <div data-testid="fmp-err" className="text-sm bg-coral-50 border border-coral-200 text-navy-700 rounded-xl px-3 py-3 flex items-start gap-2">
                <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-coral-500" />
                <div className="flex-1">{errMsg}</div>
              </div>
            )}
            <button
              onClick={snapAndSend}
              data-testid="fmp-snap"
              className="w-full bg-coral-300 text-white py-3 rounded-full font-semibold hover:bg-coral-400 inline-flex items-center justify-center gap-2"
            >
              <Camera className="w-4 h-4" /> Snap and find us
            </button>
            <label className="block text-center text-xs text-navy-700/60 underline cursor-pointer" data-testid="fmp-file-fallback-label">
              or upload a photo from your phone
              <input type="file" accept="image/*" capture="user"
                onChange={onFileFallback}
                data-testid="fmp-file-fallback"
                className="hidden" />
            </label>
          </div>
        )}

        {step === "searching" && (
          <div data-testid="fmp-searching" className="bg-white rounded-3xl soft-shadow p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-coral-300 mx-auto" />
            <p className="mt-4 text-navy-700/70">Comparing your face to every photo… hang on ✨</p>
          </div>
        )}

        {step === "results" && result && (
          <div data-testid="fmp-results" className="space-y-4">
            {/* Detected faces strip */}
            <div className="bg-white rounded-3xl soft-shadow p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-coral-500 font-bold">
                    {isGroup ? `${faceCount} people found in your selfie` : "Found you"}
                  </div>
                  <div className="font-serif italic text-navy-700 text-2xl">
                    {result.match_count} photo{result.match_count === 1 ? "" : "s"}
                  </div>
                </div>
                <button onClick={reset} data-testid="fmp-retry-inline"
                  className="text-xs text-navy-700/60 hover:text-navy-700 inline-flex items-center gap-1 underline">
                  <RotateCcw className="w-3 h-3" /> New selfie
                </button>
              </div>

              {/* Face filter chips */}
              <div data-testid="fmp-filters" className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => setActiveFilter("all")}
                  data-testid="fmp-filter-all"
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    activeFilter === "all" ? "bg-navy-700 text-white" : "bg-cream-100 text-navy-700/70 hover:bg-cream-200"
                  }`}
                >
                  All ({result.match_count})
                </button>
                {isGroup && togetherCount > 0 && (
                  <button
                    onClick={() => setActiveFilter("together")}
                    data-testid="fmp-filter-together"
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 transition-colors ${
                      activeFilter === "together" ? "bg-coral-300 text-white" : "bg-cream-100 text-navy-700/70 hover:bg-cream-200"
                    }`}
                  >
                    <Users className="w-3 h-3" /> Together ({togetherCount})
                  </button>
                )}
                {(result.query_faces || []).map((q) => (
                  <button
                    key={q.index}
                    onClick={() => setActiveFilter(q.index)}
                    data-testid={`fmp-filter-face-${q.index}`}
                    className={`pl-1 pr-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${
                      activeFilter === q.index ? "bg-navy-700 text-white" : "bg-cream-100 text-navy-700/70 hover:bg-cream-200"
                    }`}
                  >
                    <img src={q.crop} alt="" className="w-6 h-6 rounded-full object-cover" />
                    {isGroup ? `Person ${q.index + 1}` : "Me"} ({perFaceCount[q.index] || 0})
                  </button>
                ))}
              </div>

              {filteredMatches.length === 0 ? (
                <div className="text-center py-6">
                  <ImageOff className="w-8 h-8 text-navy-700/30 mx-auto" />
                  <p className="mt-3 text-sm text-navy-700/60">
                    No photos match this filter. Try another face or "All".
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {filteredMatches.map((m) => (
                    <a key={m.id}
                       href={m.file_url || `/g/${slug}#${m.id}`}
                       target="_blank" rel="noreferrer"
                       data-testid={`fmp-match-${m.id}`}
                       className="relative aspect-square rounded-lg overflow-hidden bg-cream-200 hover:opacity-90 transition-opacity group">
                      <img
                        src={m.file_url}
                        alt=""
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        className="w-full h-full object-cover"
                      />
                      {/* "Together" badge when every detected face is in this photo */}
                      {isGroup && (m.matched_faces || []).length === faceCount && (
                        <div className="absolute top-1 right-1 bg-coral-300 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
                          <Users className="w-2.5 h-2.5" /> All
                        </div>
                      )}
                    </a>
                  ))}
                </div>
              )}
              <div className="mt-4 text-[11px] text-navy-700/40 text-center">
                Match confidence: {(result.best_similarity * 100).toFixed(0)}% best match
              </div>
            </div>
          </div>
        )}

        {step === "error" && (
          <div data-testid="fmp-error" className="bg-white rounded-3xl soft-shadow p-6 text-center">
            <ImageOff className="w-8 h-8 text-red-400 mx-auto" />
            <p className="mt-3 text-navy-700/70 text-sm">{errMsg}</p>
            <Link to={`/g/${slug}`} className="mt-4 inline-block text-sm text-coral-500 underline">← Back to gallery</Link>
          </div>
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default FindMyPhotosPage;
